import { compact, uniq } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import {
	NonEditableProperties,
	NonEditablePropertyNames,
} from "../../types/NonEditableProperties";
import { ModelId } from "../../types/ModelId";
import { Base, Language } from "..";
import { ModelEventConstructor } from "../../utils/ModelEventConstructor";

const logger = createLogger({ name: "CustomerApp" });

class CustomerApp extends Base {
	public static fromResponse(data: any): CustomerApp.Model {
		const companyIds = uniq<number>(
			data.customerAppToCompany.map(({ company }) => company?.id),
		);

		return {
			id: data.id,
			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,

			name: data.name,
			active: data.active,
			default: data.default,
			root: data.root,

			mainSettings: data.mainSettings,
			orderSettings: data.orderSettings,
			interfaceSettings: CustomerApp.normalizeInterfaceSettings(
				data.interfaceSettings,
			),
			companyIds,
			taxiServiceIds: [],
		};
	}

	/** Old records may not contain newer blocks (e.g. communication) — fill them with defaults. */
	public static normalizeInterfaceSettings(
		value?: Partial<CustomerApp.Model.InterfaceSettings> | null,
	): CustomerApp.Model.InterfaceSettings {
		const def = defaultCustomerAppInterfaceSettings;
		const communication = value?.communication;

		return {
			...def,
			...(value ?? {}),
			balance: { ...def.balance, ...(value?.balance ?? {}) },
			communication: {
				...def.communication,
				...(communication ?? {}),
				callDispatcher: {
					...def.communication.callDispatcher,
					...(communication?.callDispatcher ?? {}),
				},
				messengers: {
					...def.communication.messengers,
					...(communication?.messengers ?? {}),
				},
			},
		};
	}

	/** Drop empty phone / link rows before sending to the server. */
	private static cleanInterfaceSettings(
		value: CustomerApp.Model.InterfaceSettings,
	): CustomerApp.Model.InterfaceSettings {
		const { communication } = value;
		if (!communication) return value;

		const notEmpty = (item: string) => !!item?.trim();

		return {
			...value,
			communication: {
				...communication,
				callDispatcher: {
					...communication.callDispatcher,
					phones: (communication.callDispatcher?.phones ?? [])
						.map((phone) => phone.trim())
						.filter(notEmpty),
				},
				messengers: {
					...communication.messengers,
					links: (communication.messengers?.links ?? [])
						.map((link) => link.trim())
						.filter(notEmpty),
				},
			},
		};
	}

	public static toRequest(
		model: CustomerApp.New | CustomerApp.Modified,
		force = false,
	): any {
		const companyIds = model.companyIds?.length
			? model.companyIds
			: undefined;

		return {
			force,
			name: model.name,
			active: model.active,
			default: model.default,

			mainSettings: model.mainSettings,
			orderSettings: model.orderSettings,
			interfaceSettings: model.interfaceSettings
				? CustomerApp.cleanInterfaceSettings(model.interfaceSettings)
				: undefined,

			companyIds,
		};
	}

	public static async add(object: CustomerApp.New, force?: boolean) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.customerApp.create(
						CustomerApp.toRequest(object, force),
					),
				{ silent: false, error: true },
			);

			logger.info("CustomerApp store", { object, res });
			return res;
		} catch (err: any) {
			if (err.message.includes("force")) return false;
			return true;
		}
	}

	public static async update(object: CustomerApp.Modified, force?: boolean) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.customerApp.update(
						object.id,
						CustomerApp.toRequest(object, force),
					),
				{ silent: false, error: true },
			);

			logger.info("CustomerApp update", { res, object });

			if (res?.id) return this.fromResponse(res);

			return res;
		} catch (err: any) {
			if (err.message.includes("force")) return false;
			return true;
		}
	}

	public static async remove(ids: number[] | number) {
		this.request((prpc) => prpc.theirsModel.customerApp.delete(ids));
	}

	public static async subscribe(
		params: CustomerApp.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<CustomerApp.Model>,
	): Promise<Subscription<CustomerApp.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[CustomerApp] subscribe", { params, state });
				const items = compact(state?.models || []);
				onUpdate({
					...state,

					models: items.map(this.fromResponse),
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.customerApp.subscribe({
					params,
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "CustomerApp.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: CustomerApp.SubscribeOptions) =>
				subscription.update(options),
		} as Subscription<CustomerApp.SubscribeOptions>;
	}
}

export enum CustomerAppDistanceCalculationMethod {
	STRAIGHT_LINE = "straight_line",
	OSM = "osm",
}

export const defaultCustomerAppMainSettings: CustomerApp.Model.MainSettings = {
	map: {
		active: true,
	},
	orderTime: {
		active: true,
		radius: 5_000,
		calcMethod: CustomerAppDistanceCalculationMethod.STRAIGHT_LINE,
		minTime: 300_000,
	},
};

export const defaultCustomerAppOrderSettings: CustomerApp.Model.OrderSettings =
	{
		create: {
			active: true,
		},
		update: {
			active: true,
			broadcast: {
				active: true,
				orderDate: true,
				additionalCost: true,
				orderNotes: true,
				paymentMethod: true,
				carClass: true,
				services: true,
				points: {
					active: true,
					add: true,
					update: true,
					remove: true,
				},
			},
			accepted: {
				active: true,
				orderNotes: true,
				paymentMethod: true,
				timeExecutor: 30_000,
				points: {
					active: true,
					add: true,
					update: true,
					remove: true,
				},
			},
			inProgress: {
				active: true,
				orderNotes: true,
				timeExecutor: 30_000,
				points: {
					active: true,
					add: true,
					update: true,
					remove: true,
				},
			},
		},
	};

export const defaultCustomerAppInterfaceSettings: CustomerApp.Model.InterfaceSettings =
	{
		balance: {
			active: true,
		},
		communication: {
			active: true,
			callDispatcher: {
				active: true,
				phones: [],
			},
			messengers: {
				active: true,
				links: [],
			},
		},
	};

declare namespace CustomerApp {
	interface Model extends NonEditableProperties {
		name: Record<Language, string>;
		active: boolean;
		default: boolean;
		readonly root?: boolean;

		interfaceSettings: CustomerApp.Model.InterfaceSettings;
		mainSettings: CustomerApp.Model.MainSettings;
		orderSettings: CustomerApp.Model.OrderSettings;

		companyIds: number[];
		taxiServiceIds: number[];
	}

	type New = Omit<Model, NonEditablePropertyNames | "customerAppToCompany">;
	type Modified = Partial<New> & { id: ModelId };

	interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<
			Pick<CustomerApp.Model, "name" | "active" | "default" | "createdAt">
		> {
		name?: string;
		active?: boolean;
		default?: boolean;
		companyIds?: number[];
	}

	namespace Model {
		interface MainSettings extends Record<string, any> {
			map: { active: boolean };
			orderTime: MainSettings.OrderTime;
		}

		namespace MainSettings {
			interface OrderTime {
				active: boolean;
				radius: number;
				calcMethod: CustomerAppDistanceCalculationMethod;
				minTime: number;
			}
		}

		interface OrderSettings extends Record<string, any> {
			create: OrderSettings.Create;
			update: OrderSettings.Update;
		}

		namespace OrderSettings {
			interface Points {
				active: boolean;
				add: boolean;
				update: boolean;
				remove: boolean;
			}

			interface Broadcast {
				active: boolean;
				orderDate: boolean;
				additionalCost: boolean;
				orderNotes: boolean;
				paymentMethod: boolean;
				carClass: boolean;
				services: boolean;

				points: Points;
			}

			interface Accepted {
				active: boolean;
				orderNotes: boolean;
				paymentMethod: boolean;
				timeExecutor: number;
				points: Points;
			}

			interface InProgress {
				active: boolean;
				orderNotes: boolean;
				timeExecutor: number;
				points: Points;
			}

			interface Create {
				active: boolean;
			}

			interface Update {
				active: boolean;
				broadcast: Broadcast;
				accepted: Accepted;
				inProgress: InProgress;
			}
		}

		interface InterfaceSettings extends Record<string, any> {
			balance: InterfaceSettings.Balance;
			communication: InterfaceSettings.Communication;
		}
		namespace InterfaceSettings {
			interface Balance {
				active: boolean;
			}

			interface Communication {
				active: boolean;
				callDispatcher: { active: boolean; phones: string[] };
				messengers: { active: boolean; links: string[] };
			}
		}
	}
}

export default CustomerApp;
